import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./CustomAutocomplete.css";
import api from "../../api";
import InteractionItem from "./InteractionItem";
import { FiClock } from "react-icons/fi";

const CustomAutocomplete = ({ 
  value, 
  onChange, 
  onBlur, 
  suggestions, 
  chipStyle, 
  peopleData = [], 
  fieldType = null,
  currentValue = ""
}) => {
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [hoveredSuggestion, setHoveredSuggestion] = useState(null);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const [showInteractions, setShowInteractions] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [loadingInteractions, setLoadingInteractions] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [inputWidth, setInputWidth] = useState("auto");
  const hideTooltipTimeoutRef = useRef(null);
  const showInteractionsTimeoutRef = useRef(null);

  useEffect(() => {
    if (value) {
      const filtered = suggestions.filter((s) =>
        s.toLowerCase().includes(value.toLowerCase())
      );
      
      // Sort suggestions: exact matches first (yellow), then partial matches
      const exactMatches = [];
      const partialMatches = [];
      
      filtered.forEach(s => {
        const isExactMatch = s.toLowerCase() === value.toLowerCase();
        if (isExactMatch) {
          exactMatches.push(s);
        } else {
          partialMatches.push(s);
        }
      });
      
      setFilteredSuggestions([...exactMatches, ...partialMatches]);
    } else {
      setFilteredSuggestions([]);
    }
    
    // Reset hovered suggestion when filtered suggestions change
    setHoveredSuggestion(null);
    setShowInteractions(false);
    setInteractions([]);
  }, [value, suggestions]);

  useEffect(() => {
    if (showSuggestions && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      let longestWidth = 0;
      if (filteredSuggestions.length > 0) {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        const inputEl = containerRef.current.querySelector("input");
        const computedStyle = window.getComputedStyle(inputEl);
        context.font = computedStyle.font;
        longestWidth = filteredSuggestions.reduce((max, suggestion) => {
          const width = context.measureText(suggestion).width;
          return Math.max(max, width);
        }, 0);
      }
      const extraPadding = 32; // Increased for padding + scrollbar space
      const calculatedWidth = longestWidth > 0 ? longestWidth + extraPadding : 200; // Minimum width of 200px
      setDropdownStyle({
        position: "absolute",
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: `${calculatedWidth}px`,
        minWidth: "150px",
        fontSize: "12px",
        zIndex: 9999,
        padding: "4px 0",
        margin: 0,
        listStyle: "none",
      });
    }
  }, [showSuggestions, value, filteredSuggestions]);

  useEffect(() => {
    if (chipStyle) {
      const extraCh = 2; 
      setInputWidth(`${value.length + extraCh}ch`);
    } else {
      setInputWidth("auto");
    }
  }, [value, chipStyle]);

  // Helper function to get person details for a suggestion
  const getPersonDetails = (suggestion) => {
    if (!suggestion || !fieldType || !peopleData.length) return null;
    
    const person = peopleData.find(p => {
      const fieldValue = p[fieldType];
      return fieldValue && fieldValue.toLowerCase() === suggestion.toLowerCase();
    });
    
    return person;
  };

  // Check if a suggestion is an exact match
  const isExactMatch = (suggestion) => {
    if (!suggestion || !value) return false;
    return suggestion.toLowerCase() === value.toLowerCase();
  };

  // Handle mouse enter to calculate tooltip position
  const handleSuggestionMouseEnter = (index, event) => {
    // Safety check for valid index
    if (index < 0 || index >= filteredSuggestions.length) return;
    
    // Clear any pending hide timeout
    if (hideTooltipTimeoutRef.current) {
      clearTimeout(hideTooltipTimeoutRef.current);
      hideTooltipTimeoutRef.current = null;
    }
    
    setHoveredSuggestion(index);
    
    if (event && event.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top,
        left: rect.right + 8
      });
    }
  };

  const handleSuggestionMouseLeave = () => {
    // Delay hiding to allow moving mouse to tooltip
    hideTooltipTimeoutRef.current = setTimeout(() => {
      if (!isHoveringTooltip) {
        setHoveredSuggestion(null);
      }
    }, 100);
  };

  const fetchInteractions = async (personId) => {
    if (!personId) return;
    
    setLoadingInteractions(true);
    try {
      const response = await api.get(`/interactions/${personId}`);
      setInteractions(response.data || []);
    } catch (error) {
      console.error('Error fetching interactions:', error);
      setInteractions([]);
    } finally {
      setLoadingInteractions(false);
    }
  };

  const handleTooltipMouseEnter = () => {
    // Clear any pending hide timeout
    if (hideTooltipTimeoutRef.current) {
      clearTimeout(hideTooltipTimeoutRef.current);
      hideTooltipTimeoutRef.current = null;
    }
    setIsHoveringTooltip(true);
    
    // Delay showing interactions to avoid unnecessary API calls
    showInteractionsTimeoutRef.current = setTimeout(() => {
      if (hoveredSuggestion !== null && 
          hoveredSuggestion >= 0 && 
          hoveredSuggestion < filteredSuggestions.length &&
          filteredSuggestions[hoveredSuggestion]) {
        const person = getPersonDetails(filteredSuggestions[hoveredSuggestion]);
        if (person && person.id) {
          setShowInteractions(true);
          fetchInteractions(person.id);
        }
      }
    }, 500); // 500ms delay before showing interactions
  };

  const handleTooltipMouseLeave = () => {
    // Clear show interactions timeout
    if (showInteractionsTimeoutRef.current) {
      clearTimeout(showInteractionsTimeoutRef.current);
      showInteractionsTimeoutRef.current = null;
    }
    
    setIsHoveringTooltip(false);
    setHoveredSuggestion(null);
    setShowInteractions(false);
    setInteractions([]);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hideTooltipTimeoutRef.current) {
        clearTimeout(hideTooltipTimeoutRef.current);
      }
      if (showInteractionsTimeoutRef.current) {
        clearTimeout(showInteractionsTimeoutRef.current);
      }
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      if (activeSuggestionIndex < filteredSuggestions.length - 1) {
        setActiveSuggestionIndex(activeSuggestionIndex + 1);
      }
    } else if (e.key === "ArrowUp") {
      if (activeSuggestionIndex > 0) {
        setActiveSuggestionIndex(activeSuggestionIndex - 1);
      }
    } else if (e.key === "Enter") {
      if (filteredSuggestions.length > 0) {
        onChange(filteredSuggestions[activeSuggestionIndex]);
        setShowSuggestions(false);
      }
      e.preventDefault();
    }
  };

  return (
    <div className="custom-autocomplete" ref={containerRef} style={{ position: "relative" }}>
      <input
        ref={inputRef}
        autoFocus
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setShowSuggestions(true);
          setActiveSuggestionIndex(0);
        }}
        onBlur={() => {
          setTimeout(() => {
            setShowSuggestions(false);
            onBlur();
          }, 100);
        }}
        onKeyDown={handleKeyDown}
        style={
          chipStyle
            ? {
                padding: "2px 8px",
                borderRadius: "12px",
                backgroundColor: "#e0e0e0",
                border: "none",
                outline: "none",
                cursor: "text",
                display: "inline-block",
                width: inputWidth,
                fontSize: "12px"
              }
            : {}
        }
      />
      {showSuggestions && filteredSuggestions.length > 0 && (
        <>
          {ReactDOM.createPortal(
            <ul className="autocomplete-suggestions" style={dropdownStyle}>
              {filteredSuggestions.map((suggestion, index) => {
                const exact = isExactMatch(suggestion);
                const isActive = index === activeSuggestionIndex;
                
                const itemClasses = [
                  "autocomplete-suggestion-item",
                  exact && "exact-match",
                  isActive && "active"
                ].filter(Boolean).join(" ");
                
                return (
                  <li
                    key={index}
                    className={itemClasses}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onChange(suggestion);
                      setShowSuggestions(false);
                    }}
                    onMouseEnter={(e) => handleSuggestionMouseEnter(index, e)}
                    onMouseLeave={handleSuggestionMouseLeave}
                  >
                    {suggestion}
                  </li>
                );
              })}
            </ul>,
            document.body
          )}
          {hoveredSuggestion !== null && 
           hoveredSuggestion >= 0 && 
           hoveredSuggestion < filteredSuggestions.length &&
           filteredSuggestions[hoveredSuggestion] &&
           getPersonDetails(filteredSuggestions[hoveredSuggestion]) && ReactDOM.createPortal(
            <div 
              className="autocomplete-tooltip"
              style={{
                top: `${tooltipPosition.top}px`,
                left: `${tooltipPosition.left}px`
              }}
              onMouseEnter={handleTooltipMouseEnter}
              onMouseLeave={handleTooltipMouseLeave}
            >
              {(() => {
                const person = getPersonDetails(filteredSuggestions[hoveredSuggestion]);
                return (
                  <>
                    {person.name && <div><strong>Name:</strong> <span>{person.name}</span></div>}
                    {person.email && <div><strong>Email:</strong> <span>{person.email}</span></div>}
                    {person.phone && <div><strong>Phone:</strong> <span>{person.phone}</span></div>}
                    {person.company && <div><strong>Company:</strong> <span>{person.company}</span></div>}
                    {person.status && <div><strong>Status:</strong> <span>{person.status}</span></div>}
                    {(person.first_name || person.last_name) && (
                      <div>
                        <strong>Assigned to:</strong> <span>{person.first_name} {person.last_name}</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>,
            document.body
          )}
          {showInteractions && 
           hoveredSuggestion !== null && 
           hoveredSuggestion >= 0 && 
           hoveredSuggestion < filteredSuggestions.length &&
           filteredSuggestions[hoveredSuggestion] && ReactDOM.createPortal(
            <div 
              className="autocomplete-interactions-panel"
              style={{
                top: `${tooltipPosition.top}px`,
                left: `${tooltipPosition.left + 350}px` // Position to the right of tooltip
              }}
              onMouseEnter={handleTooltipMouseEnter}
              onMouseLeave={handleTooltipMouseLeave}
            >
              <div className="interactions-panel-header">
                <FiClock size={16} />
                <span>Interaction History</span>
              </div>
              <div className="interactions-panel-content">
                {loadingInteractions ? (
                  <div className="interactions-loading">Loading...</div>
                ) : interactions.length > 0 ? (
                  interactions.map((interaction, index) => (
                    <InteractionItem 
                      key={interaction.id || index} 
                      interaction={interaction}
                    />
                  ))
                ) : (
                  <div className="interactions-empty">No interactions yet</div>
                )}
              </div>
            </div>,
            document.body
          )}
        </>
      )}
    </div>
  );
};

export default CustomAutocomplete;